/**
 * @version 1.0.0
 * @created 2026-10-03
 * @description [Component] Custom Toast — Card 기반 stack toast (addToast / closeAll / back)
 */
import type { ReactNode } from "react";
import type { ButtonProps } from "@heroui/react";
import Router from "next/router";
import { useRef, useEffect, useSyncExternalStore } from "react";
import type { AnimationPlaybackControls } from "framer-motion";
import { AnimatePresence, motion, animate, useMotionValue, useTransform } from "framer-motion";
import { Icon } from "@iconify/react";
// Components ( HeroUI )
import { Button, Card, CardBody } from "@heroui/react";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ToastOptions {
  title?: ReactNode;
  description?: ReactNode;
  color?: ButtonProps["color"];
  /** ms, 0이면 자동으로 닫히지 않음. 시간 초과 시 back() */
  timeout?: number;
  shouldShowTimeoutProgress?: boolean;
  endContent?: ReactNode;
  /** true면 우측 상단 X 버튼 표시, 누르면 back() */
  isClosable?: boolean;
  /** true면 stack 맨 아래에 추가 — 다른 toast 가 모두 닫혀야 보임 (선택 toast 등) */
  isLowPriority?: boolean;
  onClose?: () => void;
}

interface ToastItem extends ToastOptions {
  id: number;
}

// ─── Store ───────────────────────────────────────────────────────────────────

const EMPTY: ToastItem[] = [];

let stack: ToastItem[] = EMPTY;
let nextId = 0;
const listeners = new Set<() => void>();

function setStack(next: ToastItem[]) {
  stack = next;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);

  return () => listeners.delete(listener);
}

// ─── Navigation ──────────────────────────────────────────────────────────────
// 페이지 이동 중 추가된 toast 는 대기 → 이동 완료 후 표시 (refresh 직후 성공 toast 가 사라지지 않도록)
// 다른 주소로 이동할 때만 이전 페이지 toast 정리 (refresh = 같은 주소는 유지)

let isNavigating = false;
let pending: ToastItem[] = [];

function insert(item: ToastItem) {
  setStack(item.isLowPriority ? [item, ...stack] : [...stack, item]);
}

if (typeof window !== "undefined") {
  const flush = () => {
    const items = pending;

    isNavigating = false;
    pending = [];
    items.forEach(insert);
  };

  Router.events.on("routeChangeStart", (url: string) => {
    isNavigating = true;
    if (url !== Router.asPath) closeAll();
  });
  Router.events.on("routeChangeComplete", flush);
  Router.events.on("routeChangeError", flush);
}

/** 해당 id toast만 닫는다 */
export function closeToast(id: number) {
  pending = pending.filter((t) => t.id !== id);

  const target = stack.find((t) => t.id === id);

  if (!target) return;
  setStack(stack.filter((t) => t.id !== id));
  target.onClose?.();
}

export function addToast(options: ToastOptions) {
  const item = { ...options, id: ++nextId };

  if (isNavigating) pending.push(item);
  else insert(item);

  return item.id;
}

export function closeAll() {
  const closed = stack;

  setStack(EMPTY);
  closed.forEach((t) => t.onClose?.());
}

/**
 * 맨 위 토스트인지 확인한다.
 * @param id Toast ID.
 * @returns Whether the toast is on top.
 */
export function isTopToast(id: number) {
  return stack[stack.length - 1]?.id === id;
}

/** 맨 위 toast를 닫고 이전 toast를 다시 띄운다 */
export function back() {
  const top = stack[stack.length - 1];

  if (top) closeToast(top.id);
}

// ─── Component ───────────────────────────────────────────────────────────────

const TEXT_CLASS: Record<NonNullable<ButtonProps["color"]>, string> = {
  default: "text-green-600",
  primary: "text-primary",
  secondary: "text-secondary",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
};

const COLOR_CLASS: Record<NonNullable<ButtonProps["color"]>, string> = {
  default: "border-default-200",
  primary: "border-primary bg-primary-50",
  secondary: "border-secondary bg-secondary-50",
  success: "border-success bg-success-50",
  warning: "border-warning bg-warning-50",
  danger: "border-danger bg-danger-50",
};
const PROGRESS_CLASS: Record<NonNullable<ButtonProps["color"]>, string> = {
  default: "bg-default-400/20",
  primary: "bg-primary/20",
  secondary: "bg-secondary/20",
  success: "bg-success/20",
  warning: "bg-warning/20",
  danger: "bg-danger/20",
};

function ToastCard({ toast }: { toast: ToastItem }) {
  const color = toast.color ?? "default";
  const timeout = toast.timeout ?? 0;

  // 남은 시간(0→1) 하나로 닫기 timer + 진행바를 같이 구동 → hover 시 pause / 벗어나면 play
  const progress = useMotionValue(0);
  const width = useTransform(progress, (v) => `${v * 100}%`);
  const controlsRef = useRef<AnimationPlaybackControls | null>(null);

  // 다시 맨 위로 올라오면 remount 되므로 timer도 처음부터 다시 시작
  useEffect(() => {
    if (timeout <= 0) return;
    controlsRef.current = animate(progress, 1, { duration: timeout / 1000, ease: "linear", onComplete: () => closeToast(toast.id) });

    return () => controlsRef.current?.stop();
  }, [toast.id, timeout]);

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      initial={{ opacity: 0, y: -12 }}
      className="relative group"
      transition={{ duration: 0.15 }}
      whileHover={toast.isClosable ? { y: 8 } : undefined}
      onHoverStart={() => controlsRef.current?.pause()}
      onHoverEnd={() => controlsRef.current?.play()}
    >
      <Card className={`w-[calc(100vw-2rem)] max-w-sm ${COLOR_CLASS[color]}`} shadow="md">
        {timeout > 0 && toast.shouldShowTimeoutProgress && <motion.div className={`absolute inset-y-0 left-0 ${PROGRESS_CLASS[color]}`} style={{ width }} />}
        <CardBody className="relative flex flex-row items-center gap-3 pl-6 py-3.5">
          <div className={`flex flex-col flex-1 min-w-0 gap-1 ${toast.color ? TEXT_CLASS[toast.color] : ""}`}>
            {toast.title && <p className="text-sm font-medium">{toast.title}</p>}
            {toast.description && <p className="text-sm">{toast.description}</p>}
          </div>
          {toast.endContent}
        </CardBody>
      </Card>
      {/* Card가 overflow-hidden이라 밖에 둠 — HeroUI처럼 모서리에 걸치고 hover 시 표시 */}
      {toast.isClosable && (
        <Button
          isIconOnly
          aria-label="닫기"
          className={`scale-90 absolute -right-1 -top-1 z-10 h-5 w-5 min-w-5 border opacity-0 shadow-sm pointer-events-none transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 ${COLOR_CLASS[color]} ${TEXT_CLASS[color]}`}
          radius="full"
          size="sm"
          onPress={back}
        >
          <Icon icon="mdi:close" width={12} />
        </Button>
      )}
    </motion.div>
  );
}

// ─── Container ───────────────────────────────────────────────────────────────

/** _app 등 최상단에 한 번 mount. 모바일 상단 가운데 / sm 이상 우측 상단 */
export function ToastContainer() {
  const toasts = useSyncExternalStore(subscribe, () => stack, () => EMPTY);
  // 층 순서: 화면 < Navbar(z-40) < 선택 toast(z-[45]) < 모달(z-50) < custom toast(z-[100]) — 같은 자리에 겹쳐 표시
  const low = toasts.filter((t) => t.isLowPriority).at(-1);
  const top = toasts.filter((t) => !t.isLowPriority).at(-1);

  return (
    <>
      <div className="fixed left-1/2 top-1 z-[45] -translate-x-1/2 sm:left-auto sm:right-3 sm:translate-x-0">
        <AnimatePresence mode="wait">{low && <ToastCard key={low.id} toast={low} />}</AnimatePresence>
      </div>
      {/* data-react-aria-top-layer: 모달이 열려 있어도 aria-hidden / 바깥 클릭(모달 닫힘) 처리에서 제외 */}
      <div data-react-aria-top-layer="true" className="fixed left-1/2 top-1 z-[100] -translate-x-1/2 sm:left-auto sm:right-3 sm:translate-x-0">
        <AnimatePresence mode="wait">{top && <ToastCard key={top.id} toast={top} />}</AnimatePresence>
      </div>
    </>
  );
}
